import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { render } from 'components/Render';
import { Alert, Row, Select, Space, Tooltip } from 'antd';
import useRegionList from 'components/StoreSelector/hooks/useRegionList';
import { uniq } from 'lodash-es';
import React, { ElementType, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import toLowerCase from 'utils/toLowerCase';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import { DashboardName, DashboardType, Scene } from 'services/api/dashboard';
import { isAeProduct, isChatppProduct } from 'productLine';
import StoreSelector from './StoreSelector';
import { DashboardFilterCtx, DashboardFilterType } from './useFilter';
import { DepartmentsSelect } from './DepartmentSelect';
import ExportData from './ExportData';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import { cx } from '@emotion/css';
import { DashboardPlatformSelectorFeature, DashboardStoreSelectorFeature } from 'utils/featureFlag';
import { platformLabelMap } from 'services/config/platform';
import dayjs from 'dayjs';
import { QaPairSubmitterSelect } from './QaPairSubmitterSelect';

const StickyContainer = styled.div`
  margin-bottom: 16px;
  background-color: var(--theme-border-1);
  overflow: hidden;
`;
const Container = styled.div`
  background-color: var(--fett-bg-panel);
  border-radius: 0 0 4px;
  padding: 12px 16px;
  .ant-select .ant-select-selector {
    background-color: var(--fett-bg-input) !important;
    border-color: var(--fett-border-default) !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: var(--fett-text-secondary);
  }
  .ant-select-arrow,
  .ant-select-clear {
    color: var(--fett-text-tertiary);
  }
  .ant-picker {
    background-color: var(--fett-bg-input);
    border-color: var(--fett-border-default);
    color: var(--fett-text-secondary);
  }
  .ant-picker-suffix,
  .ant-picker-separator {
    color: var(--fett-text-tertiary);
  }
  .ant-btn-default {
    background-color: var(--fett-bg-input);
    border-color: var(--fett-border-default);
    color: var(--fett-text-secondary);
  }
`;
const ReadonlyPlatform = styled.span`
  margin-right: 6px;
`;
const selectCls = css`
  min-width: 120px;
`;

export interface RightExtraRenderType {
  value: DashboardFilterType;
  onChange: React.Dispatch<React.SetStateAction<DashboardFilterType>>;
}

interface Props {
  children: React.ReactNode;
  initialValue: DashboardFilterType;

  rightExtra: ElementType<any>;
  showPlatformAll?: boolean;
  dashboardName: DashboardName;
  scene: Scene;
  className?: string;

  dashboardType?: DashboardType;
  exportNodeRender?: (filterCtx: DashboardFilterType) => ReactNode;
}

export const usePlatformSelect = (props: {
  department?: Dashboard.Department;
  departments?: Dashboard.Department[];
  noDepartment?: boolean;
  scene: Scene;
  dashboardName?: DashboardName;
  showPlatformAll?: boolean;
  value?: General.PlatformUpperCase | 'ALL';
  onChange: (value: General.PlatformUpperCase | 'ALL') => void;
  className?: string;
  platforms?: General.PlatformUpperCase[];
}) => {
  const {
    department,
    departments,
    noDepartment = false,
    scene,
    dashboardName,
    showPlatformAll,
    value,
    onChange,
    className,
    platforms,
  } = props;

  const { t } = useTranslate();

  const regionInfo = useRegionList({
    departmentId: department?.id,
    departmentIds: useMemo(() => departments?.map(({ id }) => id), [departments]),
    scene,
  });

  const platformOptions = useMemo(() => {
    const { platformList } = regionInfo;

    return platformList
      .filter((platform) => {
        return platforms ? platforms.includes(platform) : true;
      })
      .filter((x) => {
        if ((isAeProduct && x !== 'ALIEXPRESS') || (isChatppProduct && x === 'ALIEXPRESS')) {
          return false;
        }

        if (x === 'WA_BIZ') {
          return false;
        }

        const departmentList = departments || (department ? [department] : []);

        if (departmentList.length === 0) {
          return noDepartment;
        }

        return departmentList.some((department) => {
          const { platformStoreScopes } = department;

          const target = platformStoreScopes.find((_) => _.platform === x);

          return target && target.storeCount > 0;
        });
      })
      .map((x) => {
        const label = platformLabelMap[toLowerCase(x)];

        if (
          x === 'TOKOPEDIA' &&
          dashboardName &&
          !['OrderOverview', 'SalesConversion'].includes(dashboardName)
        ) {
          return {
            disabled: true,
            value: x,
            label: (
              <Tooltip title={t('dashboard_tokopedia_tips2')}>
                <div>{label}</div>
              </Tooltip>
            ),
          };
        }

        if (x === 'MERCADO' && dashboardName === 'SalesConversion') {
          return {
            disabled: true,
            value: x,
            label: (
              <Tooltip title={t('dashboard-no-support-mercado-tips')}>
                <div>{label}</div>
              </Tooltip>
            ),
          };
        }

        return {
          value: x,
          label,
        };
      });
  }, [regionInfo, department, departments, noDepartment, dashboardName, t, platforms]);

  const platformOptionsWithAll = useMemo(() => {
    if (!showPlatformAll) {
      return platformOptions;
    }
    return [{ label: t('ViewDashboard.全部平台'), value: 'ALL' }, ...platformOptions];
  }, [platformOptions, t, showPlatformAll]);

  const platformSelect =
    platformOptions.length === 1 ? (
      <ReadonlyPlatform>{platformOptions[0].label}</ReadonlyPlatform>
    ) : (
      <Select
        className={cx(selectCls, className)}
        value={value}
        options={platformOptionsWithAll}
        onChange={onChange}
      />
    );

  return {
    t,
    regionInfo,
    platformOptions,
    platformOptionsWithAll,
    platformSelect,
  };
};

export const useStoreTypeSelect = (props: {
  value?: Dashboard.ReceptionScore.StoreType;
  onChange: (value?: Dashboard.ReceptionScore.StoreType) => void;
  className?: string;
}) => {
  const { value, onChange, className } = props;

  const { t } = useTranslate();

  const storeTypeOptions = useMemo(() => {
    return [
      {
        label: t('ViewShops.LOCAL'),
        value: 'LOCAL',
      },
      {
        label: t('ViewDashboard.跨境店铺'),
        value: 'CROSS',
      },
    ];
  }, [t]);

  const storeTypeSelect = (
    <Select
      placeholder={t('ViewDashboard.店铺类型')}
      options={storeTypeOptions}
      className={cx(selectCls, className)}
      value={value}
      allowClear
      onChange={onChange}
    />
  );

  return {
    t,
    storeTypeOptions,
    storeTypeSelect,
  };
};

const getPlatformsByDashboardName = (dashboardName: DashboardName) => {
  if (dashboardName === 'QaPair') {
    return ['SHOPEE', 'LAZADA', 'TIKTOK', 'TIKTOK_US', 'KWAI'] as General.PlatformUpperCase[];
  }
  return undefined;
};

export const useStoreInfoSelect = (props: {
  regionInfo: ReturnType<typeof usePlatformSelect>['regionInfo'];
  department?: Dashboard.Department;
  departments?: Dashboard.Department[];
  platform?: General.PlatformUpperCase | 'ALL';
  platformOptions: ReturnType<typeof usePlatformSelect>['platformOptions'];
  storeType?: Dashboard.ReceptionScore.StoreType;
  storeInfo: DashboardFilterType['storeInfo'];
  onChange: (storeInfo: DashboardFilterType['storeInfo']) => void;
  scene: Scene;
  className?: string;
}) => {
  const {
    regionInfo,
    department,
    departments,
    platform,
    platformOptions,
    storeType,
    storeInfo,
    onChange,
    scene,
    className,
  } = props;

  const regions = useMemo(() => {
    if (!regionInfo.regionsMap) {
      return [];
    }

    const getFilteredRegion = (platform: General.PlatformUpperCaseTunnel) => {
      const regionList = regionInfo.regionsMap[toLowerCase(platform)];

      const departmentList = departments || (department ? [department] : []);

      if (departmentList.length === 0) {
        return [];
      }

      const regionStoreScopes = departmentList.reduce<General.StoreScope['regionStoreScopes']>(
        (res, department) => {
          const { platformStoreScopes } = department;

          const platformTarget = platformStoreScopes.find((_) => _.platform === platform);

          if (!platformTarget || platformTarget.storeCount === 0) {
            return res;
          }

          const { regionStoreScopes } = platformTarget;

          return [...res, ...regionStoreScopes];
        },
        [],
      );

      if (
        regionStoreScopes.some(
          (scope) => scope.region === 'all' && (scope.storeIds || []).length === 0,
        )
      ) {
        return regionList;
      }

      return regionList.filter((region) =>
        regionStoreScopes.some((scope) => scope.region === region),
      );
    };

    if (!platform || platform === 'ALL') {
      return uniq(
        platformOptions.reduce<General.UpperCaseRegion[]>((res, { value: platform }) => {
          return [...res, ...getFilteredRegion(platform)];
        }, []),
      );
    }

    return getFilteredRegion(platform);
  }, [regionInfo.regionsMap, platform, platformOptions, department, departments]);

  const storeInfoSelect = (
    <StoreSelector
      key={`${platform}-${storeType}`}
      filter={{
        storeInfo,
        platform,
        storeType,
        department,
        departments,
      }}
      onChange={onChange}
      regions={regions}
      scene={scene}
      className={className}
    />
  );

  return {
    regions,
    storeInfoSelect,
  };
};

export const mercadoNewDataDate = '2024-08-01';
export const mercadoNewDataDays = 31;

const FilterProvider = (props: Props) => {
  const {
    children,
    initialValue,
    rightExtra,
    showPlatformAll,
    dashboardName,
    scene,
    className,
    dashboardType = 'order',
    exportNodeRender,
  } = props;

  const { DASHBOARD_TOKOPEDIA_TIPS } = useTriggerMapValue();
  const createTrigger = useCreateTrigger();

  const { t } = useTranslate();

  const [filter, setFilter] = useState<DashboardFilterType>(initialValue);
  const { onChange: onChangeCurrency } = useDashboardCurrency();
  const fnRef = useRef({
    onChangeCurrency,
  });
  fnRef.current.onChangeCurrency = onChangeCurrency;

  const getPermission = useGetPermission();

  const { platform, storeType, departments, storeInfo } = filter;

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments,
    scene,
    dashboardName,
    showPlatformAll,
    value: platform,
    platforms: getPlatformsByDashboardName(dashboardName),
    onChange: (value) => {
      setFilter((pre) => ({
        ...pre,
        platform: value,
        storeInfo: {},
        dateRange:
          dashboardName === 'OrderLoss' &&
          value === 'MERCADO' &&
          pre.dateRange[0].isBefore(mercadoNewDataDate, 'days') &&
          pre.dateRange[1].isSameOrAfter(mercadoNewDataDate, 'days')
            ? [dayjs(mercadoNewDataDate), pre.dateRange[1]]
            : pre.dateRange,
      }));
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: storeType,
    onChange: (storeType) => {
      setFilter((pre) => ({
        ...pre,
        storeType,
        storeInfo: {},
      }));
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform,
    departments,
    platformOptions,
    storeType,
    storeInfo,
    onChange: (storeInfo) =>
      setFilter((pre) => ({
        ...pre,
        storeInfo,
      })),
    scene,
  });

  useEffect(() => {
    if (filter.platform) {
      return;
    }
    if (showPlatformAll) {
      setFilter((pre: any) => ({ ...pre, platform: 'ALL' }));
      return;
    }
    if (!platformOptions.length) {
      return;
    }
    const platform = platformOptions[0].value;
    setFilter((pre: any) => ({ ...pre, platform }));
  }, [platformOptions, filter, showPlatformAll]);

  const contextValue = useMemo(() => {
    const platforms = platformOptions.map((_) => _.value);

    const has = (_platform: General.PlatformUpperCaseTunnel) =>
      filter.platform === _platform ||
      ((!filter.platform || filter.platform === 'ALL') && platforms.includes(_platform));

    return {
      filter,
      platforms,
      hasTokopedia: has('TOKOPEDIA'),
      hasMercado: has('MERCADO'),
    };
  }, [filter, platformOptions]);

  const canExportData =
    dashboardName !== 'PerformanceAgent' &&
    dashboardName !== 'PerformanceRobot' &&
    dashboardName !== 'AIReplyRobotReply' &&
    getPermission('履约数据导出');

  return (
    <DashboardFilterCtx.Provider value={contextValue}>
      <StickyContainer className={className}>
        <Container>
          <Row justify="space-between" style={{ gap: 12 }}>
            <Space wrap size={isAeProduct ? 0 : undefined} style={{ maxWidth: '100%' }}>
              <DepartmentsSelect
                style={isAeProduct ? { display: 'none' } : { minWidth: '140px' }}
                dashboardType={dashboardType}
                value={departments}
                onChange={(departments) => {
                  setFilter((pre) => ({
                    ...pre,
                    departments,
                    platform: undefined,
                    storeInfo: {},
                  }));
                }}
                onClear={
                  dashboardName === 'QaPair'
                    ? () => {
                        setFilter((pre) => ({
                          ...pre,
                          departments: [],
                          platform: undefined,
                          storeInfo: {},
                        }));
                      }
                    : undefined
                }
              />
              {dashboardName === 'QaPair' && (
                <QaPairSubmitterSelect
                  value={filter.userIds}
                  departments={departments}
                  onChange={(userIds) => setFilter((pre) => ({ ...pre, userIds }))}
                />
              )}
              {DashboardPlatformSelectorFeature && platformSelect}
              {DashboardStoreSelectorFeature && storeTypeSelect}
              {storeInfoSelect}
            </Space>
            <Space wrap style={{ maxWidth: '100%' }}>
              {exportNodeRender
                ? exportNodeRender(filter)
                : canExportData && (
                    <ExportData
                      initialValue={{
                        ...filter,
                      }}
                      scene={scene}
                      dashboardName={dashboardName}
                      type={dashboardName}
                      showPlatformAll={showPlatformAll}
                    />
                  )}
              {render({
                onChange: setFilter,
                value: filter,
                dashboardName,
                as: rightExtra,
              })}
            </Space>
          </Row>
        </Container>
        {!DASHBOARD_TOKOPEDIA_TIPS && contextValue.hasTokopedia && (
          <Alert
            banner
            closable
            type="error"
            message={t('dashboard_tokopedia_tips3')}
            onClose={() => createTrigger({ trigger: 'DASHBOARD_TOKOPEDIA_TIPS' })}
          />
        )}
        {dashboardName === 'SalesConversion' && contextValue.hasMercado && (
          <Alert banner closable type="info" message={t('dashboard-no-support-mercado-tips')} />
        )}
        {dashboardName === 'OrderLoss' &&
          contextValue.filter.platform === 'MERCADO' &&
          initialValue.dateRange[1]
            .subtract(mercadoNewDataDays - 1, 'days')
            .isBefore(mercadoNewDataDate, 'days') && (
            <Alert
              banner
              closable
              type="warning"
              message={`由于计算规则调整，自${mercadoNewDataDate}起增加展示Mercado打包、揽收、买家收货损耗节点的分析数据`}
            />
          )}
      </StickyContainer>
      {dashboardName === 'QaPair' ? children : (departments?.length || 0) > 0 ? children : null}
    </DashboardFilterCtx.Provider>
  );
};

export default FilterProvider;
